/**
 * WordPress dependencies
 */
import { Button } from '@safe-wordpress/components';
import { useDispatch, useSelect } from '@safe-wordpress/data';
import {
	createInterpolateElement,
	useEffect,
	useState,
} from '@safe-wordpress/element';
import { _x, sprintf } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { last } from 'lodash';
import {
	store as NAB_DATA,
	usePageAttribute,
	usePluginSetting,
} from '@nab/data';
import { formatNumber } from '@nab/i18n';
import type { QuotaBoost } from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';

const MESSAGE_DURATION_IN_MS = 2500;
const SCANNING_MESSAGES = [
	_x( 'Preparing your workspace…', 'text', 'nelio-ab-testing' ),
	_x( 'Reviewing your homepage…', 'text', 'nelio-ab-testing' ),
	_x( 'Looking for opportunities…', 'text', 'nelio-ab-testing' ),
	_x( 'Almost there…', 'text', 'nelio-ab-testing' ),
] as const;

export const ScanningHomepageBody = (): JSX.Element => {
	const [ messageIndex, setMessageIndex ] = useState( 0 );
	const [ _, setStatus ] = usePageAttribute(
		'welcome/status',
		'homepage-scan'
	);
	const scanStatus = useSelect(
		( select ) => select( NAB_DATA ).getSuggestionGenerationState().status,
		[]
	);

	const { generateExperimentSuggestions } = useDispatch( NAB_DATA );

	const [ quotaBoost ] = usePageAttribute( 'welcome/quota-boost', {
		value: 0,
		expirationDate: '',
	} );

	const homeUrl = usePluginSetting( 'homeUrl' );
	useEffect( () => {
		if ( ! homeUrl ) {
			return;
		}

		if ( scanStatus !== 'idle' ) {
			return;
		}

		void generateExperimentSuggestions( homeUrl ).finally( () => {
			if ( ! quotaBoost.value ) {
				setStatus( 'suggestions' );
			}
		} );
	}, [
		quotaBoost.value,
		homeUrl,
		scanStatus,
		generateExperimentSuggestions,
		setStatus,
	] );

	useEffect( () => {
		setTimeout( () => {
			setMessageIndex( messageIndex + 1 );
		}, MESSAGE_DURATION_IN_MS );
	}, [ messageIndex ] );

	const viewSuggestions = () => setStatus( 'suggestions' );
	const isBusy = 'opportunity-scan-ready' !== scanStatus;
	return (
		<div className="nab-scanning-homepage">
			{ quotaBoost.value ? (
				<ScanningWithBoost quotaBoost={ quotaBoost } />
			) : (
				<ScanningWithoutBoost />
			) }

			<div className="nab-policy-body__actions">
				<Button
					className="nab-policy-body__next"
					variant="primary"
					isBusy={ isBusy }
					disabled={ isBusy }
					onClick={ viewSuggestions }
				>
					{ isBusy
						? SCANNING_MESSAGES[ messageIndex ] ??
						  last( SCANNING_MESSAGES )
						: _x( 'Continue »', 'command', 'nelio-ab-testing' ) }
				</Button>
			</div>
		</div>
	);
};

// ============
// HELPER VIEWS
// ============

const ScanningWithBoost = ( { quotaBoost }: { quotaBoost: QuotaBoost } ) => {
	const days = differenceInDays(
		new Date(),
		new Date( quotaBoost.expirationDate )
	);

	return (
		<>
			<p className="nab-scanning-homepage__intro">
				{ sprintf(
					/* translators: %s: Icon. */
					_x(
						'%s Activation Boost Unlocked',
						'text',
						'nelio-ab-testing'
					),
					'🚀'
				) }
			</p>

			<div className="nab-scanning-homepage__content">
				{ createInterpolateElement(
					sprintf(
						/* translators: %1$s: Number of page views. %2$d: Number of days. */
						_x(
							'You can run tests with up to <strong>%1$s page views</strong> during your first <strong>%2$d days</strong>.',
							'text',
							'nelio-ab-testing'
						),
						formatNumber( quotaBoost.value ),
						days
					),
					{ strong: <strong /> }
				) }
			</div>
		</>
	);
};

const ScanningWithoutBoost = () => (
	<>
		<p className="nab-scanning-homepage__intro">
			{ createInterpolateElement(
				sprintf(
					/* translators: %s: Plugin name (Nelio A/B Testing). */
					_x( '%s is almost ready!', 'text', 'nelio-ab-testing' ),
					'<strong>Nelio A/B Testing</strong>'
				),
				{ strong: <strong /> }
			) }
		</p>

		<div className="nab-scanning-homepage__content">
			{ _x(
				'We’re taking a quick look at your site and setting everything up for your first tests.',
				'text',
				'nelio-ab-testing'
			) }
		</div>
	</>
);

// =======
// HELPERS
// =======

function differenceInDays( date1: Date, date2: Date ): number {
	const msPerDay = 1000 * 60 * 60 * 24;
	return Math.round(
		Math.abs( date2.getTime() - date1.getTime() ) / msPerDay
	);
}
